"use client";

import { motion } from "framer-motion";
import { Icon16LinkOutline } from "@linqx/icons/web";
import { Language } from "@linqx/i18n";
import { LogoWithText } from "@/components/logo";
import { cn } from "@/lib/utils";

const XIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const InstagramIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);

const TikTokIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z" />
  </svg>
);

const EmailIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
  </svg>
);

const socialLinks = [
  {
    icon: XIcon,
    href: "https://twitter.com/getlinqx",
    labelKey: "landing_footer_social_x",
  },
  {
    icon: InstagramIcon,
    href: "https://instagram.com/getlinqx",
    labelKey: "landing_footer_social_instagram",
  },
  {
    icon: TikTokIcon,
    href: "https://tiktok.com/@getlinqx",
    labelKey: "landing_footer_social_tiktok",
  },
  {
    icon: EmailIcon,
    href: "mailto:support@getlinqx.com",
    labelKey: "landing_footer_social_email",
  },
];

const legalLinks = [
  { labelKey: "landing_footer_link_privacy", href: "/privacy" },
  { labelKey: "landing_footer_link_terms", href: "/terms" },
  { labelKey: "landing_footer_link_eula", href: "/eula" },
];

const companyLinks = [
  { labelKey: "landing_footer_link_about", href: "#about" },
  { labelKey: "landing_footer_link_features", href: "#features" },
  {
    labelKey: "landing_footer_link_contact",
    href: "mailto:support@getlinqx.com",
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
};

export function Footer() {
  return (
    <footer className="relative overflow-hidden">
      {/* Animated gradient line */}
      <motion.div
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1, ease: "easeOut" }}
        className="h-px bg-gradient-to-r from-transparent via-border to-transparent origin-center"
      />

      {/* Main footer content */}
      <div className="bg-muted/30 py-16">
        <div className="container mx-auto px-4">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-8"
          >
            {/* Brand column */}
            <motion.div variants={itemVariants} className="md:col-span-1">
              <motion.a
                href="/"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="inline-block mb-4"
              >
                <LogoWithText size={32} />
              </motion.a>
              <p className="text-sm text-muted-foreground mb-6 max-w-xs">
                {Language.getKey("landing_footer_tagline")}
              </p>

              {/* Social links */}
              <div className="flex items-center gap-2">
                {socialLinks.map(({ icon: Icon, href, labelKey }, index) => (
                  <motion.a
                    key={labelKey}
                    href={href}
                    target={href.startsWith("mailto:") ? undefined : "_blank"}
                    rel={
                      href.startsWith("mailto:")
                        ? undefined
                        : "noopener noreferrer"
                    }
                    initial={{ opacity: 0, scale: 0.5 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    whileHover={{ scale: 1.15, y: -3, rotate: 5 }}
                    whileTap={{ scale: 0.9 }}
                    className="p-2.5 rounded-xl bg-card border border-border hover:border-foreground/30 hover:bg-muted transition-all group"
                    aria-label={Language.getKey(labelKey)}
                  >
                    <Icon className="text-muted-foreground group-hover:text-foreground transition-colors" />
                  </motion.a>
                ))}
              </div>

              <p className="text-xs text-muted-foreground mt-6">
                {Language.replaceVarsString(
                  Language.getKey("landing_footer_rights"),
                  { year: String(new Date().getFullYear()) },
                )}
              </p>
            </motion.div>

            {/* Company links */}
            <motion.div variants={itemVariants}>
              <h4 className="font-semibold mb-4 text-sm">
                {Language.getKey("landing_footer_col_company")}
              </h4>
              <ul className="space-y-3">
                {companyLinks.map(({ labelKey, href }, index) => (
                  <motion.li
                    key={labelKey}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 + index * 0.1 }}
                  >
                    <a
                      href={href}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1 group"
                    >
                      <motion.span
                        whileHover={{ x: 3 }}
                        className="inline-block"
                      >
                        {Language.getKey(labelKey)}
                      </motion.span>
                      <Icon16LinkOutline className="opacity-0 group-hover:opacity-100 transition-opacity" />
                    </a>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Legal links */}
            <motion.div variants={itemVariants}>
              <h4 className="font-semibold mb-4 text-sm">
                {Language.getKey("landing_footer_col_legal")}
              </h4>
              <ul className="space-y-3">
                {legalLinks.map(({ labelKey, href }, index) => (
                  <motion.li
                    key={labelKey}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                  >
                    <a
                      href={href}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1 group"
                    >
                      <motion.span
                        whileHover={{ x: 3 }}
                        className="inline-block"
                      >
                        {Language.getKey(labelKey)}
                      </motion.span>
                    </a>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Coming Soon / App badges */}
            <motion.div variants={itemVariants}>
              <h4 className="font-semibold mb-4 text-sm">
                {Language.getKey("landing_footer_col_get_app")}
              </h4>
              <div className="space-y-3">
                <AppStoreBadge store="ios" index={0} />
                <AppStoreBadge store="android" index={1} />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </footer>
  );
}

interface AppStoreBadgeProps {
  store: "ios" | "android";
  index: number;
}

const AppleIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
);

const PlayStoreIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 20.5v-17c0-.59.34-1.11.84-1.35L13.69 12l-9.85 9.85c-.5-.25-.84-.76-.84-1.35m13.81-5.38L6.05 21.34l8.49-8.49 2.27 2.27m3.35-4.31c.34.27.55.69.55 1.19s-.21.92-.55 1.18l-2.27 1.31-2.5-2.49 2.5-2.5 2.27 1.31M6.05 2.66l10.76 6.22-2.27 2.27-8.49-8.49z" />
  </svg>
);

function AppStoreBadge({ store, index }: AppStoreBadgeProps) {
  const isIOS = store === "ios";

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ delay: 0.4 + index * 0.15 }}
      whileHover={{ scale: 1.03, x: 5 }}
      whileTap={{ scale: 0.98 }}
      className={cn(
        "relative flex items-center gap-3 px-4 py-3 rounded-xl",
        "bg-card border border-border hover:border-foreground/30",
        "cursor-pointer group transition-colors",
      )}
    >
      <motion.div
        className="p-2 rounded-lg bg-muted group-hover:bg-foreground/10 transition-colors"
        whileHover={{ rotate: [0, -10, 10, 0] }}
        transition={{ duration: 0.3 }}
      >
        {isIOS ? (
          <AppleIcon className="h-5 w-5 text-foreground" />
        ) : (
          <PlayStoreIcon className="h-5 w-5 text-foreground" />
        )}
      </motion.div>
      <div>
        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
          {Language.getKey("landing_footer_coming_soon")}
        </p>
        <p className="text-sm font-semibold group-hover:text-foreground transition-colors">
          {isIOS
            ? Language.getKey("landing_footer_app_store")
            : Language.getKey("landing_footer_google_play")}
        </p>
      </div>
    </motion.div>
  );
}
